import React, { useId, useState } from "react";
import { WarningIcon, XIcon } from "@phosphor-icons/react";
import type {
  DocumentLineage,
  LineageMember,
} from "./services/useDocumentMetadata";
import { currentVersionStatus } from "./lineageStatus";
import { useViewerToolbarBottom } from "./viewerTopChrome";

// The bar pinned over the top of the document for a document that is not
// its lineage's current content (#13681). Details holds the full version
// list, but Details is closed by default in a record's pane, so a reader
// could not tell an intermediate version from the final one. This says so
// over the document itself, where it stays while the document scrolls, and
// offers Details and the latest. The design is Hunter's (Claude Design,
// boards "F. Pinned", 2026-10-19): one sentence, a white pill, an outline
// triangle in dark yellow; no amber, which read as a pumpkin. The reader can
// dismiss it once they know ("it doesnt block what the user sees and they
// can acknowledge they know", Hunter): for this open of this document only,
// and nothing is stored.

/** The bar's width, whatever the pane: it never changes when Details opens
 *  or closes, and only a pane narrower than this plus the gutters shrinks
 *  it. */
const WIDTH_PX = 700;
/** The space between the bar and the pane's edges, and between the bar and
 *  a kind's own toolbar above it. */
const GUTTER_PX = 16;

type OpenLatest = (latest: {
  blob_id: string;
  filename: string;
  blob_kind: string;
}) => void;

interface OlderVersionNoticeProps {
  /** The open document: a dismissal holds for this open of it only. */
  fileId: string | null;
  lineage: DocumentLineage | null | undefined;
  /** The document pane the bar is pinned in. */
  pane: HTMLElement | null;
  // Open the latest member in this viewer.
  onOpenLatest: OpenLatest;
  // Open Details, where the Versions section lists every version. Absent
  // while Details is already open, and the control stands down with it.
  onShowDetails?: () => void;
}

/** A live region that is on the page for every plain document, so a screen
 *  reader announces the bar when it is drawn into it (a region inserted
 *  together with its content is often not read). It draws nothing itself;
 *  its name is the bar's sentence while the bar shows. */
const OlderVersionNotice: React.FC<OlderVersionNoticeProps> = ({
  fileId,
  lineage,
  pane,
  onOpenLatest,
  onShowDetails,
}) => {
  const sentenceId = useId();
  const status = currentVersionStatus(lineage);
  return (
    <div role="status" aria-labelledby={sentenceId}>
      {status.kind === "earlier" && (
        <PinnedBar
          // Another document in the viewer is a new bar, with nothing
          // dismissed, so coming back to this one shows it again.
          key={fileId ?? ""}
          sentenceId={sentenceId}
          back={status.back}
          suggested={status.suggested}
          latest={status.latest}
          pane={pane}
          onOpenLatest={onOpenLatest}
          onShowDetails={onShowDetails}
        />
      )}
    </div>
  );
};

interface PinnedBarProps {
  sentenceId: string;
  back: number;
  suggested: boolean;
  latest: LineageMember;
  pane: HTMLElement | null;
  onOpenLatest: OpenLatest;
  onShowDetails?: () => void;
}

const PinnedBar: React.FC<PinnedBarProps> = ({
  sentenceId,
  back,
  suggested,
  latest,
  pane,
  onOpenLatest,
  onShowDetails,
}) => {
  const [dismissed, setDismissed] = useState(false);
  // Where the document starts in the pane: below the kind's own toolbar,
  // whose controls the bar never covers. Watched only while the bar shows.
  const toolbarBottom = useViewerToolbarBottom(dismissed ? null : pane);
  if (dismissed) return null;
  // A claim that rests on text similarity says only that it may be so.
  const sentence = suggested
    ? "A more recent version of this document may be available"
    : "A more recent version of this document is available";

  return (
    <div
      data-notice="older-version"
      data-version-back={back}
      style={{
        top: toolbarBottom + GUTTER_PX,
        width: `min(${WIDTH_PX}px, 100% - ${2 * GUTTER_PX}px)`,
      }}
      className="absolute left-1/2 z-20 flex min-h-[48px] -translate-x-1/2 flex-wrap items-center gap-x-3 gap-y-1 rounded-[24px] border border-[#d1d5db] bg-[var(--fp-surface)] py-[6px] pl-4 pr-2 shadow-[0_6px_20px_rgba(16,20,26,0.14)]"
    >
      {/* The sentence and the controls wrap as a pair: on a narrow pane the
          controls take a line of their own, at the right. */}
      <div className="flex min-w-0 flex-[1_1_220px] items-center gap-2">
        <WarningIcon
          aria-hidden="true"
          size={18}
          className="shrink-0 text-[#ca8a04]"
        />
        <span
          id={sentenceId}
          className="min-w-0 text-[14px] font-medium leading-5 text-[var(--fp-ink)]"
        >
          {sentence}
        </span>
      </div>
      <div className="ml-auto flex shrink-0 items-center">
        {onShowDetails && (
          <button
            type="button"
            onClick={onShowDetails}
            className="mr-1 flex h-[34px] cursor-pointer items-center whitespace-nowrap rounded-full px-2.5 text-[13.5px] font-medium text-[var(--fp-ink-3)] hover:text-[var(--fp-ink)]"
          >
            Details
          </button>
        )}
        <button
          type="button"
          onClick={() => onOpenLatest(latest)}
          className="flex h-[34px] cursor-pointer items-center whitespace-nowrap rounded-full border border-[#e5e7eb] bg-[#f3f4f6] px-3.5 text-[13.5px] font-semibold text-[var(--fp-ink)] hover:bg-[#e5e7eb]"
        >
          Open latest
        </button>
        {/* Close beside Open latest, closer than the other gaps. At phone
            its touch target reaches 44px past the 32px it draws, and the
            gap widens to the 6px that reach takes, so a tap on Open
            latest's edge is never a dismiss. */}
        <button
          type="button"
          aria-label="Dismiss"
          onClick={() => setDismissed(true)}
          className="relative ml-1 flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-full text-[var(--fp-ink-3)] hover:bg-[var(--fp-hover)] hover:text-[var(--fp-ink)] max-md:ml-1.5 max-md:after:absolute max-md:after:-inset-1.5 max-md:after:content-['']"
        >
          <XIcon aria-hidden="true" size={16} />
        </button>
      </div>
    </div>
  );
};

export default OlderVersionNotice;
